import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Container, FadeIn, SectionHeading, SectionLabel } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";
import PartnersStrip from "@/components/PartnersStrip";
import { CORPORATE_OFFERINGS, IMG } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  organisation: "",
  contact_name: "",
  email: "",
  phone: "",
  experience_type: "Immersive team building",
  group_size: "",
  event_date: "",
  budget: "",
  message: "",
};

export default function Corporate() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. Our team will reach out within 2 working days.");
      setDone(true);
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not send enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="corporate-page">
      <SEOHead
        cmsPage="corporate"
        title="Corporate Immersive Experiences · Team Building, Brunches, XR & AI Storytelling · Temple of Kulture"
        description="Bespoke immersive team building, gaming socials, AI storytelling, XR and projection productions for brands, councils, NHS and cultural institutions. London · Medway · Kent · UK-wide."
        image={IMG.corporate}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Bespoke immersive corporate experiences",
          provider: { "@type": "Organization", name: "Temple of Kulture" },
          areaServed: "United Kingdom",
        }}
      />
      <PageHero
        cmsPage="corporate"
        eyebrow="Corporate Experiences"
        title={
          <>
            Immersive worlds for <br />
            <span className="text-amber-400">organisations.</span>
          </>
        }
        subtitle="Team building, gaming socials, bespoke immersive productions and AI storytelling experiences — designed around your culture and goals."
        image={IMG.corporate}
        testId="corporate-hero"
      />

      <section className="py-24 sm:py-32">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 mb-20">
            <FadeIn className="lg:col-span-5">
              <SectionLabel>What we offer</SectionLabel>
              <SectionHeading className="mt-6">
                Bespoke. <br />
                <span className="text-amber-400">Cinematic.</span> Cultural.
              </SectionHeading>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-7">
              <ul className="grid sm:grid-cols-2 gap-3">
                {CORPORATE_OFFERINGS.map((o, i) => (
                  <li
                    key={o}
                    data-testid={`corporate-offering-${i}`}
                    className="flex items-start gap-3 border border-white/5 bg-zinc-950 p-5 hover:border-amber-400/40 transition-colors"
                  >
                    <CheckCircle2 className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                    <span className="font-display font-semibold text-sm sm:text-base">
                      {o}
                    </span>
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>

          {/* Enquiry form */}
          <FadeIn>
            <div className="border border-amber-500/15 bg-zinc-950 p-8 sm:p-12">
              <div className="grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-4">
                  <SectionLabel>Start an enquiry</SectionLabel>
                  <h3 className="mt-5 font-display font-black text-3xl sm:text-4xl leading-tight">
                    Tell us about <br />
                    <span className="text-amber-400">your world.</span>
                  </h3>
                  <p className="mt-5 text-zinc-300 leading-relaxed">
                    Request a quote, propose a partnership or describe a wild
                    idea. We'll come back to you within two working days.
                  </p>
                </div>

                <form
                  onSubmit={submit}
                  data-testid="corporate-enquiry-form"
                  className="lg:col-span-8 grid sm:grid-cols-2 gap-5"
                >
                  <Field
                    label="Organisation *"
                    name="organisation"
                    value={form.organisation}
                    onChange={onChange}
                    testId="enquiry-organisation"
                    required
                  />
                  <Field
                    label="Contact name *"
                    name="contact_name"
                    value={form.contact_name}
                    onChange={onChange}
                    testId="enquiry-contact-name"
                    required
                  />
                  <Field
                    label="Email *"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    testId="enquiry-email"
                    required
                  />
                  <Field
                    label="Phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    testId="enquiry-phone"
                  />

                  <div className="sm:col-span-1">
                    <Label>Experience type *</Label>
                    <select
                      data-testid="enquiry-experience-type"
                      required
                      name="experience_type"
                      value={form.experience_type}
                      onChange={onChange}
                      className="w-full bg-black border border-white/10 px-4 py-3.5 text-white focus:outline-none focus:border-amber-400 transition-colors"
                    >
                      {CORPORATE_OFFERINGS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                      <option>Something else</option>
                    </select>
                  </div>

                  <Field
                    label="Group size"
                    name="group_size"
                    placeholder="e.g. 30 people"
                    value={form.group_size}
                    onChange={onChange}
                    testId="enquiry-group-size"
                  />
                  <Field
                    label="Preferred date"
                    name="event_date"
                    placeholder="e.g. March 2026"
                    value={form.event_date}
                    onChange={onChange}
                    testId="enquiry-event-date"
                  />
                  <Field
                    label="Budget"
                    name="budget"
                    placeholder="e.g. £5–10k"
                    value={form.budget}
                    onChange={onChange}
                    testId="enquiry-budget"
                  />

                  <div className="sm:col-span-2">
                    <Label>What are you imagining? *</Label>
                    <textarea
                      data-testid="enquiry-message"
                      required
                      name="message"
                      rows={5}
                      value={form.message}
                      onChange={onChange}
                      className="w-full bg-black border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                      placeholder="Tell us about your team, vision, theme, location and any references you love."
                    />
                  </div>

                  <div className="sm:col-span-2 flex flex-wrap items-center gap-4 mt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="enquiry-submit-button"
                      className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
                    >
                      <Send className="h-4 w-4" />
                      {submitting ? "Sending…" : "Send enquiry"}
                    </button>
                    {done && (
                      <p
                        data-testid="enquiry-success-message"
                        className="text-amber-400 text-sm flex items-center gap-2"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Received. We'll be in touch shortly.
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </FadeIn>
        </Container>
      </section>

      {/* Real partners */}
      <section className="py-20 sm:py-24 bg-zinc-950 border-t border-amber-500/10">
        <PartnersStrip
          eyebrow="Already working with"
          title="Real partners, real ecosystems"
          copy="We don't deliver in isolation. Every immersive experience we run sits inside a wider partnership of venues, councils, funders and community networks already moving culture forward across Medway, Kent and the UK."
          variant="card"
          testId="corporate-partners"
        />
      </section>

      <NextStepsBlock
        title="Continue the journey"
        eyebrow="What's next"
        steps={[
          {
            label: "Inspiration",
            title: "See our flagship Windrush experience",
            desc: "A complete immersive case study: dramaturgy, projection, sound and audience experience.",
            cta: "Step inside",
            to: "/shows/windrush-immersive-experience",
            testid: "corporate-next-windrush",
          },
          {
            label: "Festivals & brand",
            title: "TOK Hip Hop & XR Festivals",
            desc: "Partner with two flagship 2026 festivals — sponsorship, activations & content.",
            cta: "Festivals",
            to: "/festivals",
            testid: "corporate-next-festivals",
          },
          {
            label: "Trust & governance",
            title: "Policies, safeguarding & insurance",
            desc: "Funder-ready documentation: GDPR, safeguarding, immersive safety, public liability.",
            cta: "Read policies",
            to: "/policies",
            testid: "corporate-next-policies",
          },
        ]}
      />
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2 block font-bold">
      {children}
    </label>
  );
}

function Field({ label, testId, ...rest }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testId}
        {...rest}
        className="w-full bg-black border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors"
      />
    </div>
  );
}
